import { useMemo, useState, useEffect, useCallback } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { UploadedPreviewImage, UploadedPreviewImages } from '@shared/components/PreviewWithPhoto/PreviewWithPhoto.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useDisclosure } from '@withjoy/joykit';
import { useGetEventMediaQuery } from '@apps/admin/components/PhotoUploader/PhotoUploader.graphql.generated';
import type { GetEventMediaQuery } from '@apps/admin/components/PhotoUploader/PhotoUploader.graphql.generated';
import { getLocalStorage } from '@shared/core';
import type { AddMediaArgs } from '@apps/admin/components/PhotoPickerDialog/PhotoPickerDialog.types';
import { getPreviewImageFromStorage } from './getPreviewImageFromStorage';
import { withWindow } from '@shared/utils/withWindow';
import { isActiveMediaPhoto } from '@shared/utils/eventPhoto/isPhotoTombstoned';

type MediaPhotoUnion = NonNullable<NonNullable<NonNullable<GetEventMediaQuery['eventByName']>['media']>[number]>;
type MediaPhoto = Extract<MediaPhotoUnion, { __typename?: 'MediaPhoto' }>;

type UsePreviewWithPhotoStateArgs = Readonly<{
  storageKey: string;
  /** When false, suppresses the event-media network query (e.g. behind a disabled feature flag). Defaults to true. */
  enabled?: boolean;
  /** If provided, set to 'true' on clear and removed on new selection (suppresses auto-populate). */
  clearedStorageKey?: string;
  /** Custom event name for same-tab broadcast (defaults to `joy:previewImageChanged:${storageKey}`). */
  changedEventName?: string;
  /** Custom event name for cross-instance dialog open (defaults to `joy:openPreviewPickerDialog:${storageKey}`). */
  openDialogEventName?: string;
  onPhotoSelect?: (photos: UploadedPreviewImages) => void;
  onCTAClick?: (hasExistingPhotos: boolean) => void;
  onClearAllPhotos?: () => void;
}>;

const sameImageList = (a: UploadedPreviewImages, b: UploadedPreviewImages): boolean =>
  a.length === b.length && !a.some((img, i) => img.id !== b[i].id || img.url !== b[i].url || img.width !== b[i].width || img.height !== b[i].height);

export const usePreviewWithPhotoState = ({
  storageKey,
  enabled = true,
  clearedStorageKey,
  changedEventName,
  openDialogEventName,
  onPhotoSelect,
  onCTAClick,
  onClearAllPhotos,
}: UsePreviewWithPhotoStateArgs) => {
  const { eventInfo, eventHandle } = useEventInfo();

  const changedEvent = changedEventName ?? `joy:previewImageChanged:${storageKey}`;
  const openDialogEvent = openDialogEventName ?? `joy:openPreviewPickerDialog:${storageKey}`;

  const uploadedPreviewImageKey = useMemo(() => `${storageKey}_${eventInfo?.eventId || ''}`, [storageKey, eventInfo?.eventId]);

  const storage = getLocalStorage();

  const [uploadedPreviewImages, setUploadedPreviewImages] = useState<UploadedPreviewImages>(() => {
    if (!eventInfo?.eventId) return [];
    const parsed = getPreviewImageFromStorage(storageKey, eventInfo.eventId);
    if (!parsed) return [];
    if (parsed.length === 0) {
      storage.setItem(uploadedPreviewImageKey);
    }
    return parsed;
  });

  const broadcastChanged = useCallback(() => {
    withWindow(global => global.dispatchEvent(new CustomEvent(changedEvent)));
  }, [changedEvent]);

  // Subscribe to same-tab broadcasts so every hook instance re-reads when ANY instance writes.
  useEffect(() => {
    if (!eventInfo?.eventId) return;
    const eventId = eventInfo.eventId;
    const handler = () => {
      const next = getPreviewImageFromStorage(storageKey, eventId) ?? [];
      setUploadedPreviewImages(current => (sameImageList(current, next) ? current : next));
    };
    withWindow(global => global.addEventListener(changedEvent, handler));
    return () => {
      withWindow(global => global.removeEventListener(changedEvent, handler));
    };
  }, [eventInfo?.eventId, storageKey, changedEvent]);

  const {
    data: eventMedia,
    loading: isLoadingEventMedia,
    error: eventMediaError,
  } = useGetEventMediaQuery({
    variables: { eventHandle: eventHandle || '' },
    skip: !eventHandle || !enabled,
    batchMode: 'fast',
  });

  const media = useMemo(() => {
    const mediaPhotos = (eventMedia?.eventByName?.media?.filter(m => isActiveMediaPhoto(m)) || []) as MediaPhoto[];
    return mediaPhotos.sort((a, b) => b.createdAt.milliseconds - a.createdAt.milliseconds);
  }, [eventMedia]);

  const activeMediaById = useMemo(() => {
    const map = new Map<string, MediaPhoto>();
    media.forEach(m => map.set(m.id, m));
    return map;
  }, [media]);

  // Reconcile persisted photos against the active album once media settles:
  // - drop IDs no longer present in active media (deleted/tombstoned in the album)
  // - backfill missing width/height from active album photos (legacy data)
  useEffect(() => {
    if (isLoadingEventMedia) return;
    if (!eventInfo?.eventId) return;
    if (uploadedPreviewImages.length === 0) return;
    if (eventMediaError || !eventMedia?.eventByName?.media) return;

    let changed = false;
    const reconciled: UploadedPreviewImage[] = [];
    uploadedPreviewImages.forEach(img => {
      const mediaItem = activeMediaById.get(img.id);
      if (!mediaItem) {
        // Photo was deleted from the album — drop it from storage.
        changed = true;
        return;
      }
      if (img.width && img.height) {
        reconciled.push(img);
        return;
      }
      if (!mediaItem.photo?.width || !mediaItem.photo?.height) {
        changed = true;
        return;
      }
      reconciled.push({ ...img, width: mediaItem.photo.width, height: mediaItem.photo.height });
      changed = true;
    });

    if (!changed) return;
    if (reconciled.length === 0) {
      storage.setItem(uploadedPreviewImageKey);
      // Selection emptied by album deletions — flag as cleared so auto-populate doesn't
      // re-seed other album photos; the preview falls back to template defaults instead.
      if (clearedStorageKey) {
        storage.setItem(`${clearedStorageKey}_${eventInfo?.eventId || ''}`, 'true');
      }
    } else {
      storage.setItem(uploadedPreviewImageKey, JSON.stringify(reconciled));
    }
    setUploadedPreviewImages(reconciled);
    broadcastChanged();
  }, [
    isLoadingEventMedia,
    eventMediaError,
    eventMedia?.eventByName?.media,
    eventInfo?.eventId,
    uploadedPreviewImages,
    activeMediaById,
    uploadedPreviewImageKey,
    clearedStorageKey,
    broadcastChanged,
    storage,
  ]);

  const { isOpen: isOpenPickerDialog, onOpen: onOpenPickerDialog, onClose: onClosePickerDialog } = useDisclosure();

  // Listen for cross-instance open requests
  useEffect(() => {
    const handler = () => onOpenPickerDialog();
    withWindow(global => global.addEventListener(openDialogEvent, handler));
    return () => {
      withWindow(global => global.removeEventListener(openDialogEvent, handler));
    };
  }, [onOpenPickerDialog, openDialogEvent]);

  const handlePhotoSelect = useEventCallback(({ selectedItemsId, cb }: AddMediaArgs) => {
    const idSet = new Set(selectedItemsId);
    const selectedImages = media.filter(m => idSet.has(m.id));
    selectedImages.sort((a, b) => selectedItemsId.indexOf(a.id) - selectedItemsId.indexOf(b.id));

    if (selectedImages.length > 0) {
      const data: UploadedPreviewImages = selectedImages.map(img => ({
        id: img.id,
        url: img.photo.url,
        height: img.photo.height,
        width: img.photo.width,
      }));
      storage.setItem(uploadedPreviewImageKey, JSON.stringify(data));
      if (clearedStorageKey) {
        // Remove the "user cleared" flag so auto-populate can fire again if needed
        storage.setItem(`${clearedStorageKey}_${eventInfo?.eventId || ''}`);
      }
      setUploadedPreviewImages(data);
      broadcastChanged();
      onPhotoSelect?.(data);
    }
    cb();
    onClosePickerDialog();
  });

  const handleCustomPhotoUploadCTAClick = useEventCallback(() => {
    onCTAClick?.(uploadedPreviewImages.length > 0);
    onOpenPickerDialog();
  });

  const handleClearAllPhotos = useEventCallback(() => {
    onClearAllPhotos?.();
    storage.setItem(uploadedPreviewImageKey);
    if (clearedStorageKey) {
      storage.setItem(`${clearedStorageKey}_${eventInfo?.eventId || ''}`, 'true');
    }
    setUploadedPreviewImages([]);
    broadcastChanged();
  });

  // Stable reference — PhotoPickerDialog resets its internal selection whenever the
  // selectedItems prop reference changes. Filter against active media so stale IDs
  // don't keep the CTA enabled while showing nothing in the picker.
  const selectedItemIds = useMemo(() => {
    const ids = uploadedPreviewImages.map(p => p.id);
    if (isLoadingEventMedia || eventMediaError || !eventMedia?.eventByName?.media) return ids;
    return ids.filter(id => activeMediaById.has(id));
  }, [uploadedPreviewImages, isLoadingEventMedia, eventMediaError, eventMedia?.eventByName?.media, activeMediaById]);

  /** Persist photos to storage, update state, and broadcast to same-tab instances. */
  const setStoredPhotos = useEventCallback((photos: UploadedPreviewImages) => {
    storage.setItem(uploadedPreviewImageKey, JSON.stringify(photos));
    setUploadedPreviewImages(photos);
    broadcastChanged();
  });

  return {
    uploadedPreviewImageKey,
    uploadedPreviewImages,
    selectedItemIds,
    uploadedPreviewImage: uploadedPreviewImages[0] ?? null,
    isOpenPickerDialog,
    onClosePickerDialog,
    onOpenPickerDialog,
    media,
    isLoadingEventMedia,
    handlePhotoSelect,
    handleCustomPhotoUploadCTAClick,
    handleClearAllPhotos,
    /** Exposed for wrappers that need to inject photos (e.g. auto-populate on first visit). */
    setStoredPhotos,
  };
};
